import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Input,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../../components/Common";
import {
  PaginateQuery,
  StudentV2Dto,
  SubscriptionBasicInformationDto,
  LocalizedProperty,
} from "../../../../interfaces";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../../../components";
import {
  ActionTypeEnum,
  UserAttemptType,
  VerificationMethodEnum,
} from "../../../../enums";
import {
  AddEditStudentModal,
  ChangeSubscriptionModal,
  ManualSubscribePlanModal,
  StudentsSubscriptionsModal,
} from ".";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import { SendPushNotificationModal } from "../../../NotifyUsers";
import { ParentChildrenModal } from "../ParentUsers";
import {
  useDeleteUserMutation,
  useResetUserAttemptMutation,
  useToggleBlockUserMutation,
  useToggleVerifyUserMutation,
  useUndoDeleteUserMutation,
} from "../../../../store/api/userApi";
import { useGetStudentsV2Query } from "../../../../store/api/studentApi";
import { useGetEducationSystemSelectListQuery } from "../../../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../../../store/api/courseApi";
import { useGetEducationalStageSelectListQuery } from "../../../../store/api/educationalStageApi";
import { toast } from "react-toastify";

type TProps = {};
export const StudentUsersV2Page: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showBlockUnblockModal, setShowBlockUnblockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUndoDeleteModal, setShowUndoDeleteModal] = useState(false);
  const [showVerifyUnVerifyModal, setShowVerifyUnVerifyModal] = useState(false);
  const [showResetUserAttemptModal, setShowResetUserAttemptModal] =
    useState(false);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentV2Dto | null>();
  const [filterEducationSystemId, setFilterEducationSystemId] =
    useState<string>("");
  const [filterCurriculumId, setFilterCurriculumId] = useState<string>("");
  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterEducationalStageId, setFilterEducationalStageId] =
    useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [filterPhoneVerificationStatus, setFilterPhoneVerificationStatus] =
    useState<string>("");
  const [filterDeletedStatus, setFilterDeletedStatus] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<StudentV2Dto[]>([]);
  const [toggledClearRows, setToggleClearRows] = React.useState(false);
  const [showSendPushNotificationModal, setShowSendPushNotificationModal] =
    useState(false);
  const [showAssignSubjectManuallyModal, setShowAssignSubjectManuallyModal] =
    useState(false);
  const [showChangeSubscriptionModal, setShowChangeSubscriptionModal] =
    useState(false);
  const [resetUserAttemptTitle, setResetUserAttemptTitle] =
    useState<string>("");
  const [resetUserAttemptType, setResetUserAttemptType] =
    useState<UserAttemptType>();
  const [resetUserAttemptMessage, setResetUserAttemptMessage] =
    useState<string>("");
  const [showAddEditUserModal, setShowAddEditUserModal] = useState(false);
  const [changingSubscription, setChangingSubscription] =
    useState<SubscriptionBasicInformationDto | null>(null);
  const [showChildrenModal, setShowChildrenModal] = useState(false);

  //#endregion

  //#region api
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    filterEducationSystemId || undefined,
    { skip: !filterEducationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();
  const { data: educationalStages } = useGetEducationalStageSelectListQuery(
    {
      curriculumId: filterCurriculumId || undefined,
      courseId: filterCourseId || undefined,
    },
    { skip: !filterCurriculumId || !filterCourseId },
  );
  const selectedStageGrades =
    educationalStages?.find((s) => s.id === filterEducationalStageId)
      ?.children || [];

  const {
    data: dataStudents,
    isLoading: isLoadingStudents,
    refetch,
  } = useGetStudentsV2Query(paginateQuery);

  const [
    toggleBlockUser,
    {
      isLoading: toggleBlockUserLoading,
      isSuccess: toggleBlockUserSuccess,
      error: toggleBlockUserError,
    },
  ] = useToggleBlockUserMutation();

  const [
    toggleVerifyUser,
    {
      isLoading: toggleVerifyUserLoading,
      isSuccess: toggleVerifyUserSuccess,
      error: toggleVerifyUserError,
    },
  ] = useToggleVerifyUserMutation();

  const [
    deleteUser,
    {
      isLoading: deleteUserLoading,
      isSuccess: deleteUserSuccess,
      error: deleteUserError,
    },
  ] = useDeleteUserMutation();

  const [
    undoDeleteUser,
    {
      isLoading: undoDeleteUserLoading,
      isSuccess: undoDeleteUserSuccess,
      error: undoDeleteUserError,
    },
  ] = useUndoDeleteUserMutation();

  const [
    resetUserAttempt,
    {
      isLoading: resetUserAttemptLoading,
      isSuccess: resetUserAttemptSuccess,
      error: resetUserAttemptError,
    },
  ] = useResetUserAttemptMutation();
  //#endregion

  document.title = `${t("documentTitle", { pageName: "students" })}`;

  const columns: TableColumn<StudentV2Dto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="phoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer", direction: "ltr" }}
          href={`tel:+${row.phoneNumber?.countryCode}${row.phoneNumber?.phoneNumber}`}
        >
          <u
            style={{ direction: "ltr" }}
          >{`+${row.phoneNumber?.countryCode}${row.phoneNumber?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="gender" />,
      cell: (row) => <TextColumn text={row.gender ? t(row.gender) : "-"} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="educationalLevel" />,
      minWidth: "260px",
      cell: (row) => {
        const langKey = i18n.language === "ar" ? "ar" : "en";
        const localized = (value?: LocalizedProperty | null) =>
          value?.[langKey] || value?.en || "";
        const path = [
          localized(row.educationSystem?.name),
          localized(row.curriculum?.name),
          localized(row.course?.name),
          localized(row.educationalStage?.name),
          localized(row.grade?.name || row.educationalLevel),
        ].filter(Boolean);

        if (!path.length) {
          return <TextColumn text="—" />;
        }

        return (
          <div className="py-1" style={{ maxWidth: 280 }}>
            <div className="fw-semibold text-wrap">
              {localized(row.grade?.name || row.educationalLevel) || "—"}
            </div>
            <small className="text-muted text-wrap d-block">
              {path.slice(0, -1).join(" / ") || "—"}
            </small>
          </div>
        );
      },
      sortable: false,
    },
    {
      name: <ColumnHeader title="parents" />,
      cell: (row) => (
        <>
          <div>
            <div className="my-2">
              <strong>{t("active")}: </strong>
              <strong>
                {row.parents.filter((p) => p.isDeleted == false)?.length}
              </strong>
            </div>
            <div className="my-2">
              <strong className="text-danger">{t("deleted")}: </strong>
              <strong className="text-danger">
                {row.parents.filter((p) => p.isDeleted == true)?.length}
              </strong>
            </div>
            <div className="my-2">
              <Button
                disabled={row.parents?.length === 0}
                className="btn btn-success btn-sm"
                onClick={() => onViewParents(row)}
              >
                {t("view")}
              </Button>
            </div>
          </div>
        </>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="subscriptions" />,
      cell: (row) => (
        <>
          <Button
            className="btn btn-success btn-sm"
            onClick={() => onViewSubscriptions(row)}
          >
            {t("subscriptions")}
          </Button>
          &nbsp;
          <Button
            className="btn btn-success btn-sm"
            onClick={() => onAssignSubjects(row)}
          >
            {t("manualPurchase")}
          </Button>
        </>
      ),
      sortable: false,
      width: "260px",
    },
    {
      name: <ColumnHeader title="phoneVerified" />,
      cell: (row) => (
        <>
          <Badge color={row.phoneNumber.isPhoneVerified ? "success" : "danger"}>
            {row.phoneNumber.isPhoneVerified ? t("yes") : t("no")}
          </Badge>
          &nbsp;
          <ActionButton
            color={row.phoneNumber.isPhoneVerified ? "danger" : "success"}
            onAction={() => onTogglePhoneVerify(row)}
            state={row.phoneNumber.isPhoneVerified}
            actionType={ActionTypeEnum.PHONE_VERIFY}
          />
        </>
      ),
      sortable: false,
      width: "260px",
    },
    {
      name: <ColumnHeader title="deleted" />,
      cell: (row) => (
        <Badge color={!row.isDeleted ? "success" : "danger"}>
          {row.isDeleted ? t("yes") : t("no")}
        </Badge>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: <ColumnHeader title="block" />,
      cell: (row) => (
        <Badge color={!row.isBlocked ? "success" : "danger"}>
          {row.isBlocked ? t("yes") : t("no")}
        </Badge>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      width: "100px",
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" id="dropdownMenuLink2" role="button">
            <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.INVALID_PASSWORD_ATTEMPT,
                )
              }
            >
              {t("resetInvalidLoginAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.RESET_PASSWORD_VERIFICATION_ATTEMPT,
                )
              }
            >
              {t("resetForgetPasswordAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.SIGNUP_VERIFICATION_ATTEMPT,
                )
              }
            >
              {t("resetSignupAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() => onToggleBlock(row)}
            >
              {row.isBlocked ? t("unblock") : t("block")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() => {
                if (row.isDeleted) onUndoDeleteUser(row);
                else onDelete(row);
              }}
            >
              {row.isDeleted ? t("undoDelete") : t("delete")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() => {
                setSelectedStudent(row);
                setShowAddEditUserModal(true);
              }}
            >
              {t("edit")}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    if (!toggleBlockUserLoading && toggleBlockUserSuccess) {
      setSelectedStudent(null);
      setShowBlockUnblockModal(false);
      refetch();
    }
  }, [toggleBlockUserLoading]);

  useEffect(() => {
    if (!toggleVerifyUserLoading && toggleVerifyUserSuccess) {
      setSelectedStudent(null);
      setShowVerifyUnVerifyModal(false);
      refetch();
    }
  }, [toggleVerifyUserLoading]);

  useEffect(() => {
    if (!deleteUserLoading && deleteUserSuccess) {
      setSelectedStudent(null);
      setShowDeleteModal(false);
      refetch();
    }
  }, [deleteUserLoading]);

  useEffect(() => {
    if (!undoDeleteUserLoading && undoDeleteUserSuccess) {
      setSelectedStudent(null);
      setShowUndoDeleteModal(false);
      refetch();
    }
  }, [undoDeleteUserLoading]);

  useEffect(() => {
    if (!resetUserAttemptLoading && resetUserAttemptSuccess) {
      setSelectedStudent(null);
      setShowResetUserAttemptModal(false);
      refetch();
    }
  }, [resetUserAttemptLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[educationSystemId]")) {
      filters = {
        ...filters,
        ["educationSystemId"]:
          queryParams.get("filter[educationSystemId]") || "",
      };
      setFilterEducationSystemId(filters["educationSystemId"] as string);
    }
    if (queryParams.has("filter[curriculumId]")) {
      filters = {
        ...filters,
        ["curriculumId"]: queryParams.get("filter[curriculumId]") || "",
      };
      setFilterCurriculumId(filters["curriculumId"] as string);
    }
    if (queryParams.has("filter[course]")) {
      filters = {
        ...filters,
        ["course"]: queryParams.get("filter[course]") || "",
      };
      setFilterCourseId(filters["course"] as string);
    }
    if (queryParams.has("filter[educationalStageId]")) {
      filters = {
        ...filters,
        ["educationalStageId"]:
          queryParams.get("filter[educationalStageId]") || "",
      };
      setFilterEducationalStageId(filters["educationalStageId"] as string);
    }
    if (queryParams.has("filter[grade]")) {
      filters = {
        ...filters,
        ["grade"]: queryParams.get("filter[grade]") || "",
      };
      setFilterGradeId(filters["grade"] as string);
    }
    if (queryParams.has("filter[gender]")) {
      filters = {
        ...filters,
        ["gender"]: queryParams.get("filter[gender]") || "",
      };
      setFilterGenderId(filters["gender"] as string);
    }
    if (queryParams.has("filter[phoneVerified]")) {
      filters = {
        ...filters,
        ["phoneVerified"]: queryParams.get("filter[phoneVerified]") || "",
      };
      setFilterPhoneVerificationStatus(filters["phoneVerified"] as string);
    }
    if (queryParams.has("filter[deleted]")) {
      filters = {
        ...filters,
        ["deleted"]: queryParams.get("filter[deleted]") || "",
      };
      setFilterDeletedStatus(filters["deleted"] as string);
    }

    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onViewSubscriptions = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowSubscriptionsModal(true);
  };

  const onAssignSubjects = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowAssignSubjectManuallyModal(true);
  };

  const onToggleBlock = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowBlockUnblockModal(true);
  };

  const onDelete = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowDeleteModal(true);
  };

  const onUndoDeleteUser = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowUndoDeleteModal(true);
  };

  const onTogglePhoneVerify = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowVerifyUnVerifyModal(true);
  };

  const onBlockUnblockConfirm = () => {
    toggleBlockUser({
      id: selectedStudent?.id as string,
      state: !(selectedStudent?.isBlocked as boolean),
    });
    setSelectedStudent(null);
  };

  const onResetUserAttempt = (_row: StudentV2Dto, type: UserAttemptType) => {
    setSelectedStudent(_row);
    setResetUserAttemptType(type);
    if (type === UserAttemptType.INVALID_PASSWORD_ATTEMPT) {
      setResetUserAttemptTitle(t("resetInvalidLoginAttempts"));
      setResetUserAttemptMessage(
        t("resetInvalidLoginAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedStudent?.name.ar
              : selectedStudent?.name.en,
        }),
      );
    }
    if (type == UserAttemptType.RESET_PASSWORD_VERIFICATION_ATTEMPT) {
      setResetUserAttemptTitle(t("resetForgetPasswordAttempts"));
      setResetUserAttemptMessage(
        t("resetForgetPasswordAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedStudent?.name.ar
              : selectedStudent?.name.en,
        }),
      );
    }
    if (type == UserAttemptType.SIGNUP_VERIFICATION_ATTEMPT) {
      setResetUserAttemptTitle(t("resetSignupAttempts"));
      setResetUserAttemptMessage(
        t("resetSignupAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedStudent?.name.ar
              : selectedStudent?.name.en,
        }),
      );
    }
    setShowResetUserAttemptModal(true);
  };

  const onResetUserAttemptConfirm = () => {
    if (resetUserAttemptType) {
      resetUserAttempt({
        id: selectedStudent?.id as string,
        type: resetUserAttemptType,
      });
    }
    setSelectedStudent(null);
  };

  const onDeleteConfirm = () => {
    deleteUser(selectedStudent?.id as string);
    setSelectedStudent(null);
  };

  const onUndoDeleteConfirm = () => {
    undoDeleteUser(selectedStudent?.id as string);
    setSelectedStudent(null);
  };

  const onVerifyConfirm = () => {
    toggleVerifyUser({
      userId: [selectedStudent?.id as string],
      status: !selectedStudent?.phoneNumber?.isPhoneVerified,
      method: VerificationMethodEnum.PHONE,
    });
    setSelectedStudent(null);
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column: TableColumn<StudentV2Dto>, sortDirection: string) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeEducationSystemFilter = (value: string) => {
    setFilterEducationSystemId(value);
    setFilterCurriculumId("");
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["educationSystemId"]: value,
        ["curriculumId"]: "",
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    });
  };

  const onChangeCurriculumFilter = (value: string) => {
    setFilterCurriculumId(value);
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["curriculumId"]: value,
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    });
  };

  const onChangeCourseFilter = (value: string) => {
    setFilterCourseId(value);
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["course"]: value,
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    });
  };

  const onChangeEducationalStageFilter = (value: string) => {
    setFilterEducationalStageId(value);
    setFilterGradeId("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["educationalStageId"]: value,
        ["grade"]: "",
      },
    });
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["grade"]: value,
      },
    });
  };

  const onChangeGenderFilter = (value: string) => {
    setFilterGenderId(value);
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["gender"]: value,
      },
    });
  };

  const onChangeFilterPhoneVerification = (value: string) => {
    setFilterPhoneVerificationStatus(value);
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["phoneVerified"]: value,
      },
    });
  };

  const onChangeDeletedFilter = (value: string) => {
    setFilterDeletedStatus(value);
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["deleted"]: value,
      },
    });
  };

  const onClearFilters = () => {
    setFilterEducationSystemId("");
    setFilterCurriculumId("");
    setFilterCourseId("");
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setFilterGenderId("");
    setFilterPhoneVerificationStatus("");
    setFilterDeletedStatus("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        ...paginateQuery.filter,
        ["educationSystemId"]: "",
        ["curriculumId"]: "",
        ["course"]: "",
        ["educationalStageId"]: "",
        ["grade"]: "",
        ["gender"]: "",
        ["phoneVerified"]: "",
        ["deleted"]: "",
      },
    });
  };

  const handleSelectionRows = ({
    selectedRows,
  }: {
    selectedRows: StudentV2Dto[];
  }) => {
    setSelectedRows(selectedRows);
  };

  const handleClearRows = () => {
    setToggleClearRows(!toggledClearRows);
    setSelectedRows([]);
  };

  const onSendPushNotification = () => {
    if (selectedRows.length > 0) {
      setShowSendPushNotificationModal(true);
    }
  };

  const onViewParents = (_row: StudentV2Dto) => {
    setSelectedStudent(_row);
    setShowChildrenModal(true);
  };
  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("students")} pageTitle={t("appUsers")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <Input
                        type="select"
                        value={filterEducationSystemId}
                        className="form-select"
                        aria-label={t("filterByEducationSystem")}
                        onChange={(e) =>
                          onChangeEducationSystemFilter(e.target.value)
                        }
                      >
                        <option value="">{t("filterByEducationSystem")}</option>
                        {(educationSystems || []).map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {s.value?.[langKey] || s.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <Input
                        type="select"
                        value={filterCurriculumId}
                        className="form-select"
                        aria-label={t("filterByCurriculum")}
                        disabled={!filterEducationSystemId}
                        onChange={(e) =>
                          onChangeCurriculumFilter(e.target.value)
                        }
                      >
                        <option value="">{t("filterByCurriculum")}</option>
                        {(curricula || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {c.value?.[langKey] || c.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <Input
                        type="select"
                        value={filterCourseId}
                        className="form-select"
                        aria-label={t("filterByCourse")}
                        onChange={(e) => onChangeCourseFilter(e.target.value)}
                      >
                        <option value="">{t("filterByCourse")}</option>
                        {(courses || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {c.value?.[langKey] || c.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <Input
                        type="select"
                        value={filterEducationalStageId}
                        className="form-select"
                        aria-label={t("filterByEducationalStage")}
                        disabled={!filterCurriculumId || !filterCourseId}
                        onChange={(e) =>
                          onChangeEducationalStageFilter(e.target.value)
                        }
                      >
                        <option value="">{t("filterByEducationalStage")}</option>
                        {(educationalStages || []).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.value?.[langKey] || s.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <Input
                        type="select"
                        value={filterGradeId}
                        className="form-select"
                        aria-label={t("filterByGrade")}
                        disabled={!filterEducationalStageId}
                        onChange={(e) => onChangeGradeFilter(e.target.value)}
                      >
                        <option value="">{t("filterByGrade")}</option>
                        {selectedStageGrades.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.value?.[langKey] || g.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterGenderId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeGenderFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByGender")}</option>
                          <option value="Female">{t("female")}</option>
                          <option value="Male">{t("male")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterPhoneVerificationStatus}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeFilterPhoneVerification(event.target.value)
                          }
                        >
                          <option value="">
                            {t("filterByPhoneVerification")}
                          </option>
                          <option value="verified">{t("verified")}</option>
                          <option value="unverified">{t("unverified")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterDeletedStatus}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeDeletedFilter(event.target.value)
                          }
                        >
                          <option value="">
                            {t("filterByDeletedStatus")}
                          </option>
                          <option value="active">{t("active")}</option>
                          <option value="deleted">{t("deleted")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => {
                            setPaginateQuery({ ...paginateQuery });
                            refetch();
                          }}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          disabled={selectedRows.length === 0}
                          color="success"
                          onClick={onSendPushNotification}
                        >
                          <i className="ri-notification-line me-2 align-bottom"></i>
                          {t("sendNotification")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        selectableRows
                        onSelectedRowsChange={handleSelectionRows}
                        clearSelectedRows={toggledClearRows}
                        theme={"dark"}
                        columns={columns}
                        data={dataStudents?.data || []}
                        progressPending={isLoadingStudents}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={dataStudents?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  {/* block unblock modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={
                      (selectedStudent?.isBlocked as boolean)
                        ? t("unblock")
                        : t("block")
                    }
                    description={
                      (selectedStudent?.isBlocked as boolean)
                        ? t("unblockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                        : t("blockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                    }
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={toggleBlockUserLoading}
                    show={showBlockUnblockModal}
                    onConfirmClick={() => onBlockUnblockConfirm()}
                    onCloseClick={() => {
                      setShowBlockUnblockModal(false);
                    }}
                    error={
                      toggleBlockUserError && "data" in toggleBlockUserError
                        ? (toggleBlockUserError.data as any as string)
                        : null
                    }
                  />
                  {/* verify un-verify modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={
                      (selectedStudent?.phoneNumber.isPhoneVerified as boolean)
                        ? t("markUnverified")
                        : t("markVerified")
                    }
                    description={
                      (selectedStudent?.phoneNumber.isPhoneVerified as boolean)
                        ? t("unVerifyPhoneConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                        : t("verifyPhoneConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedStudent?.name.ar
                                : selectedStudent?.name.en,
                          })
                    }
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={toggleVerifyUserLoading}
                    show={showVerifyUnVerifyModal}
                    onConfirmClick={() => onVerifyConfirm()}
                    onCloseClick={() => {
                      setShowVerifyUnVerifyModal(false);
                    }}
                    error={
                      toggleVerifyUserError && "data" in toggleVerifyUserError
                        ? (toggleVerifyUserError.data as any as string)
                        : null
                    }
                  />
                  {/* delete modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("deleting")}
                    confirmationButtonTitle={t("delete")}
                    description={t("deleteConfirmationMessage", {
                      username:
                        i18n.language === "ar"
                          ? selectedStudent?.name.ar
                          : selectedStudent?.name.en,
                    })}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={deleteUserLoading}
                    show={showDeleteModal}
                    onConfirmClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={
                      deleteUserError && "data" in deleteUserError
                        ? (deleteUserError.data as any as string)
                        : null
                    }
                  />
                  {/* undo delete modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={t("undoDelete")}
                    description={t("undoDeleteConfirmationMessage", {
                      username:
                        i18n.language === "ar"
                          ? selectedStudent?.name.ar
                          : selectedStudent?.name.en,
                    })}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={undoDeleteUserLoading}
                    show={showUndoDeleteModal}
                    onConfirmClick={() => onUndoDeleteConfirm()}
                    onCloseClick={() => {
                      setShowUndoDeleteModal(false);
                    }}
                    error={
                      undoDeleteUserError && "data" in undoDeleteUserError
                        ? (undoDeleteUserError.data as any as string)
                        : null
                    }
                  />
                  {/* reset user attempts modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={resetUserAttemptTitle}
                    description={resetUserAttemptMessage}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={resetUserAttemptLoading}
                    show={showResetUserAttemptModal}
                    onConfirmClick={() => onResetUserAttemptConfirm()}
                    onCloseClick={() => {
                      setShowResetUserAttemptModal(false);
                    }}
                    error={
                      resetUserAttemptError && "data" in resetUserAttemptError
                        ? (resetUserAttemptError.data as any as string)
                        : null
                    }
                  />
                  <AddEditStudentModal
                    student={selectedStudent}
                    show={showAddEditUserModal}
                    onCloseClick={() => {
                      setShowAddEditUserModal(false);
                      setSelectedStudent(null);
                      refetch();
                    }}
                  />
                  {selectedStudent && (
                    <StudentsSubscriptionsModal
                      studentId={selectedStudent.id}
                      show={showSubscriptionsModal}
                      onCloseClick={() => {
                        setShowSubscriptionsModal(false);
                      }}
                      onChangeSubscription={(
                        subscription: SubscriptionBasicInformationDto,
                      ) => {
                        setShowSubscriptionsModal(false);
                        setChangingSubscription(subscription);
                        setShowChangeSubscriptionModal(true);
                      }}
                    />
                  )}
                  {selectedStudent &&
                    selectedStudent.parents &&
                    selectedStudent.parents.length > 0 && (
                      <ParentChildrenModal
                        title="parents"
                        children={selectedStudent?.parents}
                        show={showChildrenModal}
                        onCloseClick={() => {
                          setShowChildrenModal(false);
                          refetch();
                        }}
                      />
                    )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <SendPushNotificationModal
          show={showSendPushNotificationModal}
          onCloseClick={() => {
            setShowSendPushNotificationModal(false);
            handleClearRows();
          }}
          mode="user"
          user={selectedRows.map((user) => ({
            id: user.id,
            name: user.name,
          }))}
        />
      )}
      {selectedStudent && (
        <ManualSubscribePlanModal
          userId={selectedStudent?.id}
          name={selectedStudent?.name}
          phoneNumber={selectedStudent?.phoneNumber}
          show={showAssignSubjectManuallyModal}
          onCloseClick={() => {
            setShowAssignSubjectManuallyModal(false);
            refetch();
          }}
        />
      )}
      {selectedStudent && changingSubscription && (
        <ChangeSubscriptionModal
          userId={selectedStudent?.id}
          name={selectedStudent?.name}
          phoneNumber={selectedStudent?.phoneNumber}
          show={showChangeSubscriptionModal}
          currentSubscription={changingSubscription}
          onCloseClick={() => {
            setShowChangeSubscriptionModal(false);
            refetch();
          }}
          onBack={function (): void {
            setShowChangeSubscriptionModal(false);
            setShowSubscriptionsModal(true);
          }}
        />
      )}
    </React.Fragment>
  );
};
